import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Card from "components/card";
import InputField from "components/fields/InputField";
import Dropdown from "components/dropdown";
import { MdFilterList, MdSearch, MdBusiness, MdPerson } from "react-icons/md";
import { FaPlane, FaTrain, FaCar, FaHotel, FaBus, FaPassport } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

// Mock data with more records
const mockCarteData = [
  {
    id: 1,
    employeeName: "John Doe",
    companyName: "Tech Corp",
    customerId: "CUST-001",
    carteNo: "CT-2024-001",
    bookingTypes: [
      {type: "flight", details: "AA123 - NYC to LAX"},
      {type: "hotel", details: "Marriott Downtown - 5 nights"},
      {type: "taxi", details: "Airport Transfer"}
    ],
    startDate: "2024-01-15",
    endDate: "2024-01-20",
    destination: "New York",
    totalAmount: "$2,450",
    status: "Active"
  },
  {
    id: 2,
    employeeName: "Jane Smith",
    companyName: "Global Inc",
    customerId: "CUST-002",
    carteNo: "CT-2024-002",
    bookingTypes: [
      {type: "train", details: "Amtrak - Chicago Express"},
      {type: "hotel", details: "Hilton Garden - 4 nights"}
    ],
    startDate: "2024-01-18",
    endDate: "2024-01-22",
    destination: "Chicago",
    totalAmount: "$1,850",
    status: "Pending"
  },
  {
    id: 3,
    employeeName: "Mike Johnson",
    companyName: "Tech Corp",
    customerId: "CUST-003",
    carteNo: "CT-2024-003",
    bookingTypes: [
      {type: "flight", details: "UA456 - LAX to SFO"},
      {type: "train", details: "Caltrain - Local Service"},
      {type: "hotel", details: "Hyatt Regency - 3 nights"},
      {type: "bus", details: "City Bus Pass"}
    ],
    startDate: "2024-01-25",
    endDate: "2024-01-30",
    destination: "Los Angeles",
    totalAmount: "$3,200",
    status: "Completed"
  },
  {
    id: 4,
    employeeName: "Sarah Wilson",
    companyName: "Global Inc",
    customerId: "CUST-004",
    carteNo: "CT-2024-004",
    bookingTypes: [
      {type: "flight", details: "DL789 - Miami to Boston"},
      {type: "taxi", details: "Airport & City Transfer"}
    ],
    startDate: "2024-02-01",
    endDate: "2024-02-05",
    destination: "Miami",
    totalAmount: "$1,950",
    status: "Active"
  },
  {
    id: 5,
    employeeName: "David Brown",
    companyName: "StartUp Ltd",
    customerId: "CUST-005",
    carteNo: "CT-2024-005",
    bookingTypes: [
      {type: "train", details: "Eurostar - London to Paris"},
      {type: "hotel", details: "Le Meridien - 3 nights"},
      {type: "bus", details: "City Tour Pass"}
    ],
    startDate: "2024-02-10",
    endDate: "2024-02-13",
    destination: "Paris",
    totalAmount: "$2,100",
    status: "Pending"
  },
  {
    id: 6,
    employeeName: "Lisa Garcia",
    companyName: "Tech Corp",
    customerId: "CUST-006",
    carteNo: "CT-2024-006",
    bookingTypes: [
      {type: "flight", details: "SW321 - Denver to Seattle"},
      {type: "hotel", details: "Four Seasons - 4 nights"},
      {type: "taxi", details: "Premium Transfer Service"}
    ],
    startDate: "2024-02-15",
    endDate: "2024-02-19",
    destination: "Seattle",
    totalAmount: "$2,800",
    status: "Active"
  }
];

const companyEmployeeMap = {
  "Tech Corp": ["John Doe", "Mike Johnson", "Lisa Garcia"],
  "Global Inc": ["Jane Smith", "Sarah Wilson"],
  "StartUp Ltd": ["David Brown"]
};

const BookingIcon = ({ type }) => {
  const iconProps = { size: 14, className: "text-white" };
  
  switch (type) {
    case "flight": return <FaPlane {...iconProps} />;
    case "railway": return <FaTrain {...iconProps} />;
    case "car": return <FaCar {...iconProps} />;
    case "hotel": return <FaHotel {...iconProps} />;
    case "visa": return <FaPassport {...iconProps} />;
    default: return null;
  }
};

const BookingTypeStack = ({ bookingTypes }) => {
  const colors = {
    flight: "bg-blue-500",
    railway: "bg-green-500", 
    car: "bg-yellow-500",
    hotel: "bg-purple-500",
    visa: "bg-red-500"
  };

  // Group bookings by type
  const groupedBookings = bookingTypes.reduce((acc, booking) => {
    if (!acc[booking.type]) {
      acc[booking.type] = [];
    }
    acc[booking.type].push(booking.details);
    return acc;
  }, {});

  return (
    <div className="flex -space-x-1">
      {Object.entries(groupedBookings).map(([type, details], index) => {
        const tooltipContent = `${type.charAt(0).toUpperCase() + type.slice(1)} (${details.length}):\n${details.join('\n')}`;
        
        return (
          <div
            key={type}
            className={`${colors[type]} rounded-full p-1.5 border-2 border-white shadow-sm cursor-pointer relative`}
            style={{ zIndex: Object.keys(groupedBookings).length - index }}
            data-tooltip-id={`booking-${type}`}
            data-tooltip-content={tooltipContent}
          >
            <BookingIcon type={type} />
            {/* {details.length > 1 && (
              <span className="absolute -top-1 -right-1 bg-white text-xs rounded-full h-4 w-4 flex items-center justify-center text-gray-700 font-bold">
                {details.length}
              </span>
            )} */}
            <Tooltip id={`booking-${type}`} style={{ whiteSpace: 'pre-line' }} />
          </div>
        );
      })}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const statusColors = {
    Active: "bg-green-100 text-green-800",
    Pending: "bg-yellow-100 text-yellow-800", 
    Completed: "bg-blue-100 text-blue-800"
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
      {status}
    </span>
  );
};

const CarteListing = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  
  const loadCartes = () => {
    const savedCartes = JSON.parse(localStorage.getItem('savedCartes') || '[]');
    const formattedCartes = savedCartes.map((carte, index) => {
      const allBookings = Object.entries(carte.bookings).reduce((acc, [type, items]) => {
        items.forEach(item => {
          acc.push({ type, details: item.name });
        });
        return acc;
      }, []);
      
      return {
        id: index + 1,
        employeeName: carte.customer.name,
        companyName: "Your Company",
        customerId: carte.customer.id,
        carteNo: `CT-${new Date(carte.timestamp).getFullYear()}-${String(index + 1).padStart(3, '0')}`,
        bookingTypes: allBookings,
        startDate: carte.timestamp.split('T')[0],
        endDate: carte.timestamp.split('T')[0],
        destination: "Multiple",
        totalAmount: "$0",
        status: "Active"
      };
    });
    
    // Combine with mock data
    setData([...formattedCartes, ...mockCarteData]);
  };

  // Load saved cartes on component mount
  React.useEffect(() => {
    loadCartes();
  }, []);

  // Real-time updates
  React.useEffect(() => {
    const handleCartesUpdate = () => {
      loadCartes();
    };

    const handleStorageChange = (e) => {
      if (e.key === 'savedCartes') {
        loadCartes();
      }
    };

    const handleWindowFocus = () => {
      loadCartes();
    };

    // Listen for custom events
    window.addEventListener('cartesUpdated', handleCartesUpdate);
    
    // Listen for localStorage changes from other tabs
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for window focus to refresh data
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('cartesUpdated', handleCartesUpdate);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [tripDate, setTripDate] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("All Companies");
  const [selectedEmployee, setSelectedEmployee] = useState("All Employees");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const availableEmployees = useMemo(() => {
    if (selectedCompany === "All Companies") {
      return ["All Employees", ...Object.values(companyEmployeeMap).flat()];
    }
    return ["All Employees", ...(companyEmployeeMap[selectedCompany] || [])];
  }, [selectedCompany]);

  const handleCompanyChange = (company) => {
    setSelectedCompany(company);
    setSelectedEmployee("All Employees");
  };

  const columns = useMemo(() => [
    columnHelper.accessor("carteNo", {
      id: "carteNo",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">CARTE NO.</p>
      ),
      cell: (info) => {
        const rowIndex = info.row.index;
        return (
          <button
            onClick={() => {
              navigate(`/admin/booking?carteId=${rowIndex + 1}`);
            }}
            className="text-sm font-mono font-bold text-navy-700 dark:text-white hover:text-blue-600 cursor-pointer"
          >
            {info.getValue()}
          </button>
        );
      },
    }),
    columnHelper.accessor("employeeName", {
      id: "employeeName",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">EMPLOYEE</p>
      ),
      cell: (info) => (
        <div className="flex items-center min-w-[140px]">
          <div className="h-7 w-7 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs mr-2">
            {info.getValue().split(' ').map(n => n[0]).join('')}
          </div>
          <p className="text-sm font-bold text-navy-700 dark:text-white truncate">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor("customerId", {
      id: "customerId",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">CUSTOMER ID</p>
      ),
      cell: (info) => (
        <p className="text-sm font-mono text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("bookingTypes", {
      id: "bookingTypes",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">BOOKINGS</p>
      ),
      cell: (info) => <BookingTypeStack bookingTypes={info.getValue()} />,
    }),
    columnHelper.accessor("destination", {
      id: "destination",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">DESTINATION</p>
      ),
      cell: (info) => (
        <p className="text-sm font-medium text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("startDate", {
      id: "tripDate",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">TRIP DATE</p>
      ),
      cell: (info) => (
        <p className="text-sm font-medium text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("totalAmount", {
      id: "totalAmount",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">AMOUNT</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">STATUS</p>
      ),
      cell: (info) => <StatusBadge status={info.getValue()} />,
    }),
  ], []);

  const filteredData = useMemo(() => {
    return data.filter(item => {
      const matchesCompany = selectedCompany === "All Companies" || item.companyName === selectedCompany;
      const matchesEmployee = selectedEmployee === "All Employees" || item.employeeName === selectedEmployee;
      const matchesDate = !tripDate || item.startDate.includes(tripDate) || item.endDate.includes(tripDate);
      
      return matchesCompany && matchesEmployee && matchesDate;
    });
  }, [data, selectedCompany, selectedEmployee, tripDate]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="mt-3">
      {/* Filters Section */}
      <Card extra="w-full p-4 mb-5">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center">
            <MdFilterList className="mr-2 text-navy-700 dark:text-white" />
            <span className="text-sm font-medium text-navy-700 dark:text-white">Filters:</span>
          </div>
          
          {/* Company Dropdown */}
          <div className="min-w-[180px]">
            <Dropdown
              button={
                <div className="flex h-10 w-full items-center justify-between rounded-lg border border-gray-200 bg-white p-2 text-sm outline-none dark:border-white/10 dark:text-white cursor-pointer hover:border-gray-300">
                  <div className="flex items-center">
                    <MdBusiness className="mr-2 text-gray-400" size={16} />
                    <span className="truncate">{selectedCompany}</span>
                  </div>
                </div>
              }
              classNames="top-12 right-0 w-full"
            >
              <div className="bg-white dark:bg-navy-700 border border-gray-200 dark:border-white/10 rounded-lg shadow-lg">
                {["All Companies", ...Object.keys(companyEmployeeMap)].map((company) => (
                  <div
                    key={company}
                    className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-white/10 cursor-pointer text-sm"
                    onClick={() => handleCompanyChange(company)}
                  >
                    {company}
                  </div>
                ))}
              </div>
            </Dropdown>
          </div>

          {/* Employee Dropdown */}
          <div className="min-w-[180px]">
            <Dropdown
              button={
                <div className="flex h-10 w-full items-center justify-between rounded-lg border border-gray-200 bg-white p-2 text-sm outline-none dark:border-white/10 dark:text-white cursor-pointer hover:border-gray-300">
                  <div className="flex items-center">
                    <MdPerson className="mr-2 text-gray-400" size={16} />
                    <span className="truncate">{selectedEmployee}</span>
                  </div>
                </div>
              }
              classNames="top-12 right-0 w-full"
            >
              <div className="bg-white dark:bg-navy-700 border border-gray-200 dark:border-white/10 rounded-lg shadow-lg">
                {availableEmployees.map((employee) => (
                  <div
                    key={employee}
                    className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-white/10 cursor-pointer text-sm"
                    onClick={() => setSelectedEmployee(employee)}
                  >
                    {employee}
                  </div>
                ))}
              </div>
            </Dropdown>
          </div>

          {/* Trip Date */}
          <div className="min-w-[150px]">
            <input
              type="date"
              value={tripDate}
              onChange={(e) => setTripDate(e.target.value)}
              className="h-10 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm outline-none dark:border-white/10 dark:text-white hover:border-gray-300"
              placeholder="Travel Date"
            />
          </div>
        </div>
      </Card>

      {/* Table Section */}
      <Card extra="w-full px-6 pb-6">
        <div className="relative flex items-center justify-between pt-4">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            Carte Listing ({filteredData.length} records)
          </div>
          <div className="relative w-80">
            <input
              type="text"
              placeholder="Search across all fields..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-3 pr-10 text-sm outline-none dark:border-white/10 dark:text-white"
            />
            <MdSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          </div>
        </div>

        <div className="mt-8 w-full">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="border-b border-gray-200">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        onClick={header.column.getToggleSortingHandler()}
                        className="cursor-pointer border-b-[1px] border-gray-200 pt-4 pb-2 px-2 text-start"
                        style={{ 
                          width: header.id === 'carteNo' ? '12%' :
                                 header.id === 'employeeName' ? '15%' : 
                                 header.id === 'customerId' ? '12%' : 
                                 header.id === 'bookingTypes' ? '15%' : 
                                 header.id === 'destination' ? '12%' : 
                                 header.id === 'tripDate' ? '15%' :
                                 header.id === 'totalAmount' ? '10%' : '9%' 
                        }}
                      >
                        <div className="items-center justify-between text-xs text-gray-200">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-white/5">
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="border-white/0 py-3 px-2"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                {'<<'}
              </button>
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {'<'}
              </button>
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {'>'}
              </button>
              <button
                className="px-3 py-1 border rounded disabled:opacity-50"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                {'>>'}
              </button>
            </div>
            <span className="flex items-center gap-1">
              <div>Page</div>
              <strong>
                {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </strong>
            </span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={e => {
                table.setPageSize(Number(e.target.value))
              }}
              className="border rounded p-1"
            >
              {[5, 10, 20, 30, 40, 50].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CarteListing;